'use strict';

import moment from 'moment-timezone';

import {isPropertyValueType} from '~/shared/src/mm/PropertySet';
import {FilterValueType} from '~/shared/src/validationSchemes/modules/overview';


/**
 * Converts and returns the given value for the given database field type.
 */
export function convertValueForType( value : boolean | number | string, type : FilterValueType ) : AttributeValue {
    let retVal;

    if ( !isPropertyValueType( type ) ) {
        switch ( type ) {
            case 'REFDIRECTION':

                break;

            case 'REFTYPEWITHDIRECTION':

                break;

            case 'STATUS':
            case 'STATUSID':
            case 'STATUSNAME':
                type = 'INTEGER';
                break;
        }
    }

    switch ( type ) {
        case 'BOOLEAN':
            retVal = convertToBoolean( value );
            break;

        case 'COORDINATE':
            retVal = convertToCoordinates( value );
            break;

        case 'CURRENCY':
        case 'DOUBLE':
        case 'DURATION':
            retVal = convertToDouble( value );
            break;

        case 'DATE':
        case 'TIME':
        case 'TIMESTAMP':
            retVal = convertToDate( value, type );
            break;

        case 'INTEGER':
            retVal = convertToInteger( value );
            break;

        case 'COLOR':
        case 'EMAIL':
        case 'IBAN':
        case 'IMAGEDATA':
        case 'JSON':
        case 'LINK':
        case 'PHONE':
        case 'STRING':
        case 'TEXT':
        case 'TIMERULE':
        default:
            retVal = convertToString( value );
            break;
    }

    return retVal;
}

/**
 * Converts the value of the valueType to string
 */
export function convertValueToString( value : AttributeValue, type : FilterValueType ) : string {
    let retVal : string = '';

    let momentDate;

    if ( !isPropertyValueType( type ) ) {
        switch ( type ) {
            case 'REFDIRECTION':

                break;

            case 'REFTYPEWITHDIRECTION':

                break;

            case 'STATUS':
            case 'STATUSID':
            case 'STATUSNAME':
                type = 'INTEGER';
                break;
        }
    }

    if ( value !== '' ) {
        if ( value === 'now' && ( type === 'DATE' || type === 'TIME' || type === 'TIMESTAMP' ) ) {
            retVal = 'now';
        }
        else {
            switch ( type ) {
                case 'BOOLEAN':
                    retVal = value
                        ? '1'
                        : '0';
                    break;

                case 'COORDINATE':
                    retVal = ( Array.isArray( value ) ? value : [] ).join( ',' );
                    break;

                case 'DATE':
                    momentDate = moment( <Date> value ).tz( 'utc' );
                    if ( momentDate?.isValid() ) {
                        retVal = momentDate.format( 'YYYY-MM-DD' );
                    }
                    break;

                case 'TIME':
                    momentDate = moment( <Date> value );
                    if ( momentDate?.isValid() ) {
                        retVal = momentDate.format( 'HH:mm:ss.SSSSSS' );
                    }
                    break;

                case 'TIMESTAMP':
                    momentDate = moment( <Date> value ).tz( 'utc' );
                    if ( momentDate?.isValid() ) {
                        retVal = momentDate.format( 'YYYY-MM-DD HH:mm:ss.SSSSSS' );
                    }
                    break;

                case 'COLOR':
                case 'CURRENCY':
                case 'DOUBLE':
                case 'DURATION':
                case 'EMAIL':
                case 'IBAN':
                case 'INTEGER':
                case 'IMAGEDATA':
                case 'JSON':
                case 'LINK':
                case 'PHONE':
                case 'STRING':
                case 'TEXT':
                case 'TIMERULE':
                default:
                    retVal = String( value );
                    break;
            }
        }
    }

    return retVal;
}

export const convertToBoolean = ( value : boolean | number | string ) : boolean | null => {
    let retVal : boolean | null = null;

    if ( value !== '' ) {
        switch ( value ) {
            case false:
            case 0:
            case '0':
            case 'f':
            case 'false':
                retVal = false;
                break;

            default:
                retVal = true;
        }
    }

    return retVal;
}

export const convertToCoordinates = ( value : boolean | number | string ) : number[] => {
    let retVal : number[] = [];

    // convert to string
    value = String( value );
    value = value.replace( / /g, '' );

    try {
        retVal = value.split( ',' ).map( v => Number( v ) );
    }
    catch ( e ) {
    }

    return retVal;
}

export const convertToDate = ( value : boolean | number | string, type : 'DATE' | 'TIME' | 'TIMESTAMP' = 'TIMESTAMP' ) : NDate => {
    let retVal : NDate,
        momentDate;

    // convert to string
    value = String( value );

    if ( value === 'now' ) {
        retVal = new Date();
    }

    else {
        // if value is a numeric value with 10 characters, starting with 1 or 2 assume it to be a unix timestamp
        // FIXME: nicht zwingend... Das ist kein guter Test. Eventuell einfach auf Integer testen?
        if ( /^[12]\d{9}$/.test( value ) ) {
            momentDate = moment( value, 'X' );
        }

        // try to parse
        else {
            // TIMESTAMP
            momentDate = moment( value, 'YYYY-MM-DD HH:mm:ss.SSSSSS' );

            if ( !momentDate?.isValid() ) {
                momentDate = moment( value, 'YYYY-MM-DD HH:mm:ss' );
            }

            if ( !momentDate?.isValid() ) {
                momentDate = moment( value, 'YYYY-MM-DD HH:mm' );
            }

            // DATE
            if ( !momentDate?.isValid() ) {
                momentDate = moment( value, 'YYYY-MM-DD' );
            }

            // TIME
            if ( !momentDate?.isValid() ) {
                momentDate = moment( value, 'HH:mm:ss.SSSSSS' );
            }

            if ( !momentDate?.isValid() ) {
                momentDate = moment( value, 'HH:mm:ss' );
            }

            if ( !momentDate?.isValid() ) {
                momentDate = moment( value, 'HH:mm' );
            }
        }

        if ( momentDate?.isValid() ) {

            if ( type === 'DATE' || type === 'TIMESTAMP' ) {
                // set to utc without adjusting
                momentDate = momentDate.tz( 'utc', true );
            }

            retVal = momentDate.toDate();
        }
        else {
            retVal = null;
        }
    }

    return retVal;
}

export const convertToDouble = ( value : boolean | number | string ) : number | null => {
    let retVal : number | null = Number( value );

    if ( Number.isNaN( retVal ) ) {
        retVal = null;
    }

    return retVal;
}

export const convertToInteger = ( value : boolean | number | string ) : number | null => {
    let retVal : number | null = parseInt( String( value ) );

    if ( Number.isNaN( retVal ) ) {
        retVal = null;
    }

    return retVal;
}

export const convertToString = ( value : boolean | number | string ) : string => {
    return String( value );
}
